import type { FormNode, FormSchema } from '../designer/types/form.types'
import { appendJournalPage } from '../shared/form/pagesModel'

export const JOURNAL_PREVIEW_GROUP_NAME = 'JournalPreview'

/** Designer-preview default: accented groups stay inline (unless the schema says otherwise). */
export function isInlineJournalPreviewGroup(group: FormNode | null): boolean {
  if (!group) return false
  return group.style?.groupChrome === 'accent'
}

/** Group `JournalPreview` on item form root (any custom document form that declares one). */
export function findJournalPreviewGroup(root: FormNode[] | undefined): FormNode | null {
  if (!root?.length) return null
  return root.find((n) => n.type === 'Group' && n.name === JOURNAL_PREVIEW_GROUP_NAME) ?? null
}

/**
 * General tab schema without the journal preview group.
 *
 * Placement precedence: an explicit `schema.journalPreviewMode` always wins;
 * otherwise the group's own chrome style decides (designer-preview default).
 */
export function splitFormSchemaForJournalPreview(
  schema: FormSchema,
): {
  generalSchema: FormSchema
  journalPreviewGroup: FormNode | null
  journalPreviewInline: boolean
} {
  const group = findJournalPreviewGroup(schema.root)
  if (!group) {
    return { generalSchema: schema, journalPreviewGroup: null, journalPreviewInline: false }
  }
  const keepOnGeneral =
    schema.journalPreviewMode === 'tab'
      ? false
      : schema.journalPreviewMode === 'inline'
        ? true
        : isInlineJournalPreviewGroup(group)
  if (keepOnGeneral) {
    return { generalSchema: schema, journalPreviewGroup: group, journalPreviewInline: true }
  }
  const generalRoot = schema.root.filter(
    (n) => !(n.type === 'Group' && n.name === JOURNAL_PREVIEW_GROUP_NAME),
  )
  if (generalRoot.some((n) => n.type === 'Pages')) {
    return {
      generalSchema: { ...schema, root: appendJournalPage(generalRoot, group) },
      journalPreviewGroup: group,
      journalPreviewInline: true,
    }
  }
  return {
    generalSchema: { ...schema, root: generalRoot },
    journalPreviewGroup: group,
    journalPreviewInline: false,
  }
}

/** Table nodes inside JournalPreview (écriture A/B, …). */
export function journalPreviewTableNodes(group: FormNode): FormNode[] {
  return (group.children ?? []).filter((n) => n.type === 'Table')
}

/** A table styled/named as a journal-preview table (chrome flag, or a `previewEcriture*` name). */
export function isJournalPreviewTable(node: FormNode): boolean {
  const name = node.name || ''
  return !!node.style?.tableChrome || /^previewEcriture/i.test(name)
}

/** Walk form root (any depth) for journal-preview tables. */
export function findJournalPreviewTables(root: FormNode[] | undefined): FormNode[] {
  const out: FormNode[] = []
  const walk = (nodes: FormNode[]) => {
    for (const n of nodes) {
      if (n.type === 'Table' && isJournalPreviewTable(n)) out.push(n)
      if (n.children?.length) walk(n.children)
    }
  }
  if (root?.length) walk(root)
  return out
}
